import React, { useState, useEffect } from 'react';
import { getPublicEvents } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [sectionContent, setSectionContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredButton, setHoveredButton] = useState(false);
  const navigate = useNavigate();

  const EVENTS_PER_PAGE = 6;

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const eventsResponse = await getPublicEvents();
      console.log('Events API Response:', eventsResponse);
      
      let eventsData = [];
      if (eventsResponse && eventsResponse.data && Array.isArray(eventsResponse.data)) {
        eventsData = eventsResponse.data;
      } else if (eventsResponse && Array.isArray(eventsResponse)) {
        eventsData = eventsResponse;
      } else if (eventsResponse && eventsResponse.events && Array.isArray(eventsResponse.events)) {
        eventsData = eventsResponse.events;
      }
      
      const activeEvents = eventsData
        .filter(event => event.isActive !== false)
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      setEvents(activeEvents);
      console.log('Events loaded:', activeEvents.length);
      
      const sectionResponse = await fetch('https://inteliheads.in/api/cms/public/content/events');
      const sectionDataResponse = await sectionResponse.json();
      if (sectionDataResponse && sectionDataResponse.data) {
        setSectionContent(sectionDataResponse.data);
      } else if (sectionDataResponse && sectionDataResponse._id) {
        setSectionContent(sectionDataResponse);
      }
      
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'Conference': '🌍',
      'Webinar': '💻',
      'Workshop': '🔧',
      'Bootcamp': '🏕️',
      'Seminar': '📚',
      'Networking': '🤝',
      'Training': '🎓'
    };
    return icons[eventType] || '📅';
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      'Conference': '#2C7A7B',
      'Webinar': '#3498db',
      'Workshop': '#F5A623',
      'Bootcamp': '#9b59b6',
      'Seminar': '#1abc9c',
      'Networking': '#e74c3c',
      'Training': '#2ecc71'
    };
    return colors[eventType] || '#9b59b6';
  };

  const formatDateTime = (date, time) => {
    if (!date) return 'Coming Soon';
    const dateObj = new Date(date);
    const dateStr = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    if (time) {
      return `${dateStr} • ${time}`;
    }
    return dateStr;
  };

  const handleViewDetails = (event) => {
    navigate(`/resource/events/${event._id}`, { 
      state: { item: event, type: 'events' } 
    });
  };

  const handleViewAllEvents = () => {
    navigate('/resources', { state: { activeTab: 'events' } });
  };

  const displayedEvents = events.slice(0, EVENTS_PER_PAGE);
  const showButton = events.length > 0;

  if (loading) {
    return (
      <section style={{ padding: '5rem 0', textAlign: 'center', background: '#FFFFFF' }}>
        <div className="container">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>Loading events...</p>
        </div>
      </section>
    );
  }

  const title = sectionContent?.title || 'Global Events & Programs';
  const subtitle = sectionContent?.subtitle || 'Webinars, certification drives, and industry conferences.';
  const backgroundColor = sectionContent?.backgroundColor || '#FFFFFF';
  const buttonText = sectionContent?.buttonText || 'Browse All Events';

  return (
    <section id="events" style={{
      padding: 'clamp(3rem, 8vw, 5rem) 0',
      background: backgroundColor,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1000'%3E%3Cpath fill='%230A192F' d='M0,0 L1000,0 L1000,1000 L0,1000 Z M200,200 L800,200 L800,800 L200,800 Z'/%3E%3Ccircle cx='500' cy='500' r='150'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '50px'
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'clamp(2rem, 6vw, 4rem)'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
            marginBottom: '0.5rem',
            color: '#0A192F',
            fontWeight: 700,
            letterSpacing: '-0.02em'
          }}>
            {title}
          </h2>
          
          <div style={{
            width: '60px',
            height: '3px',
            background: '#2C7A7B',
            margin: '1rem auto',
            borderRadius: '2px'
          }} />
          
          <p style={{
            fontSize: 'clamp(1rem, 4vw, 1.125rem)',
            color: '#5A6E7F',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {subtitle}
          </p>

          {/* Browse All Events Button */}
          {showButton && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: '2rem'
            }}>
              <button
                onClick={handleViewAllEvents}
                onMouseEnter={() => setHoveredButton(true)}
                onMouseLeave={() => setHoveredButton(false)}
                style={{
                  position: 'relative',
                  padding: '0.8rem 2rem',
                  background: hoveredButton ? 'transparent' : 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                  color: hoveredButton ? '#9b59b6' : 'white',
                  border: hoveredButton ? '2px solid #9b59b6' : 'none',
                  borderRadius: '50px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  overflow: 'hidden',
                  boxShadow: hoveredButton ? 'none' : '0 4px 15px rgba(155,89,182,0.3)'
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: hoveredButton ? 'shimmer 1.5s infinite' : 'none'
                }} />
                <i className="fas fa-calendar-alt" style={{ fontSize: '1rem' }}></i>
                <span>{buttonText}</span>
                <i className="fas fa-arrow-right" style={{ 
                  fontSize: '0.85rem',
                  transition: 'transform 0.3s ease',
                  transform: hoveredButton ? 'translateX(5px)' : 'translateX(0)'
                }}></i>
              </button>
            </div>
          )}
        </div>

        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '24px' }}>
            <i className="fas fa-calendar-alt" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#5A6E7F', fontSize: '1.1rem' }}>No upcoming events at the moment. Check back soon!</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 'clamp(1.5rem, 3vw, 2rem)',
            marginBottom: 'clamp(2rem, 5vw, 3rem)'
          }}>
            {displayedEvents.map((event, index) => {
              const eventColor = getEventTypeColor(event.type);
              
              return (
                <div
                  key={event._id || index}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  style={{
                    background: 'white',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid rgba(44,122,123,0.1)',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#2C7A7B';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = 'rgba(44,122,123,0.1)';
                  }}
                >
                  {/* Image Container */}
                  <div style={{
                    position: 'relative',
                    height: '220px',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${eventColor} 0%, #5a2d7a 100%)`
                  }}>
                    {event.imageUrl ? (
                      <img 
                        src={getFullImageUrl(event.imageUrl)} 
                        alt={event.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justifyContent: center; height: 100%; font-size: 3rem;">${getEventIcon(event.type)}</div>`;
                        }}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        fontSize: '3rem',
                        color: 'white'
                      }}>
                        {getEventIcon(event.type)}
                      </div>
                    )}
                    
                    {/* Event Type Badge */}
                    {event.type && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'rgba(0,0,0,0.7)',
                        backdropFilter: 'blur(8px)',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        color: 'white',
                        fontWeight: 600
                      }}>
                        {event.type}
                      </div>
                    )}
                    
                    {/* Virtual Badge */}
                    {event.isVirtual && (
                      <div style={{
                        position: 'absolute',
                        bottom: '1rem',
                        left: '1rem',
                        background: 'rgba(0,0,0,0.7)',
                        backdropFilter: 'blur(8px)',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                      }}>
                        <i className="fas fa-video"></i>
                        <span>Virtual</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#0A192F',
                      marginBottom: '0.5rem',
                      lineHeight: '1.3'
                    }}>
                      {event.title}
                    </h3>

                    {/* Date and Time */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.8rem',
                      color: eventColor
                    }}>
                      <i className="fas fa-calendar-alt"></i>
                      <span>{formatDateTime(event.startDate, event.startTime)}</span>
                    </div>

                    {/* Location */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                      fontSize: '0.8rem',
                      color: '#5A6E7F'
                    }}>
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{event.location || 'Online'}</span>
                    </div>

                    {/* Description */}
                    <p style={{
                      color: '#5A6E7F',
                      lineHeight: '1.6',
                      fontSize: '0.85rem',
                      marginBottom: '1rem',
                      flex: 1
                    }}>
                      {event.description && event.description.length > 120 
                        ? `${event.description.substring(0, 120)}...` 
                        : event.description || 'No description available'}
                    </p>

                    {/* Price and Seats */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      padding: '0.5rem 0',
                      borderTop: '1px solid #E2E8F0',
                      borderBottom: '1px solid #E2E8F0'
                    }}>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: eventColor
                      }}>
                        {event.price === 'Free' ? 'FREE' : event.price}
                      </span>
                      {event.seats > 0 && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: event.seats > 10 ? '#10B981' : '#F5A623'
                        }}>
                          <i className="fas fa-users"></i> {event.seats} seats
                        </span>
                      )}
                    </div>

                    {/* Agenda Preview */}
                    {event.agenda && event.agenda.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#0A192F',
                          marginBottom: '0.25rem'
                        }}>
                          <i className="fas fa-list"></i> Agenda Highlights:
                        </div>
                        {event.agenda.slice(0, 2).map((item, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.7rem',
                            color: '#5A6E7F',
                            marginBottom: '0.25rem'
                          }}>
                            <i className="fas fa-clock" style={{ color: eventColor, fontSize: '0.6rem' }}></i>
                            <span>{item.length > 35 ? `${item.substring(0, 35)}...` : item}</span>
                          </div>
                        ))}
                        {event.agenda.length > 2 && (
                          <div style={{ fontSize: '0.65rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                            +{event.agenda.length - 2} more agenda items
                          </div>
                        )}
                      </div>
                    )}

                    {/* Speakers Preview */}
                    {event.speakers && event.speakers.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#0A192F',
                          marginBottom: '0.25rem'
                        }}>
                          <i className="fas fa-users"></i> Speakers:
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.3rem'
                        }}>
                          {event.speakers.slice(0, 2).map((speaker, idx) => (
                            <span key={idx} style={{
                              fontSize: '0.7rem',
                              background: '#F7F9FC',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              color: '#5A6E7F'
                            }}>
                              {speaker.length > 20 ? `${speaker.substring(0, 20)}...` : speaker}
                            </span>
                          ))}
                          {event.speakers.length > 2 && (
                            <span style={{
                              fontSize: '0.7rem',
                              color: '#94A3B8'
                            }}>
                              +{event.speakers.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* View Details Button */}
                    <button
                      onClick={() => handleViewDetails(event)}
                      style={{
                        width: '100%',
                        marginTop: '1rem',
                        padding: '0.7rem 1.2rem',
                        background: `linear-gradient(135deg, ${eventColor} 0%, #5a2d7a 100%)`,
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 4px 12px ${eventColor}40`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      View Details <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </section>
  );
};

export default Events;